import React, { useState, useRef, useEffect, useMemo } from 'react'
import { Modal, Form, Button, Alert, Spinner, Dropdown } from 'react-bootstrap'
import styled, { css } from 'styled-components'
import { Send, MessageCircle, Bot, User, Plus, Edit2, Trash2 } from 'lucide-react'
import { useAppSelector } from '../../store/hooks'
import { selectSelectedProjectId } from '../../store/slices/projectSlice'
import { selectDatasetSources } from '../../store/slices/datasetSlice'
import { useGetProjectQuery, useGetModelTreeQuery } from '../../services/projectsApi'
import {
  useListChatsQuery,
  useCreateChatMutation,
  useUpdateChatMutation,
  useDeleteChatMutation,
  useGetChatHistoryQuery,
  useSendMessageMutation,
} from '../../services/chatApi'
import { ButtonProps } from 'react-bootstrap'
import { TransformedChatMessage } from '../../services/chatApi'

interface AIChatModalProps {
  show: boolean
  onHide: () => void
}

interface NewChatButtonProps extends ButtonProps {
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void
}

interface ChatItemProps {
  $isSelected: boolean
  onClick: (e: React.MouseEvent<HTMLDivElement>) => void
}

interface PromptCardProps {
  onClick: (e: React.MouseEvent<HTMLDivElement>) => void
}

interface StyledProps {
  theme?: any
  as?: string
  forwardedAs?: string
}

interface DisplayMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
}

const StyledModal = styled(Modal)`
  .modal-dialog {
    max-width: 1200px;
    height: 90vh;
    margin: 2rem auto;
    width: 100%;
  }

  .modal-content {
    background: #1a1a1a;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    height: 100%;
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .modal-header {
    background: linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    color: white;
    padding: 1rem 1.5rem;
    width: 100%;

    .modal-title {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-weight: 600;
      font-size: 1.2rem;
    }

    .btn-close {
      filter: invert(1);
      opacity: 0.7;

      &:hover {
        opacity: 1;
      }
    }
  }

  .modal-body {
    flex: 1;
    padding: 0;
    display: flex;
    overflow: hidden;
    width: 100%;
  }
`

const ChatSidebar = styled.div`
  width: 260px;
  min-width: 260px;
  background: #1a1a1a;
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-shrink: 0;
`

const NewChatButton = styled(Button).attrs<NewChatButtonProps & StyledProps>({
  type: 'button',
  variant: 'custom',
})<NewChatButtonProps & StyledProps>`
  margin: 1rem;
  background: rgba(85, 60, 154, 0.2);
  border: 1px solid rgba(85, 60, 154, 0.4);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(85, 60, 154, 0.3);
    border-color: rgba(85, 60, 154, 0.6);
    transform: translateY(-1px);
  }
`

const ChatList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #2a2a2a;
  }

  &::-webkit-scrollbar-thumb {
    background: #555;
    border-radius: 3px;
  }
`

const ChatItem = styled.div<ChatItemProps & StyledProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  border-radius: 8px;
  background: ${(props) => (props.$isSelected ? 'rgba(85, 60, 154, 0.3)' : 'transparent')};
  border: 1px solid
    ${(props) => (props.$isSelected ? 'rgba(85, 60, 154, 0.6)' : 'rgba(255, 255, 255, 0.1)')};
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) =>
      props.$isSelected ? 'rgba(85, 60, 154, 0.4)' : 'rgba(85, 60, 154, 0.1)'};
  }

  .chat-info {
    flex: 1;
    overflow: hidden;

    .chat-name {
      font-weight: 500;
      margin-bottom: 0.25rem;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .chat-description {
      font-size: 0.85rem;
      color: rgba(255, 255, 255, 0.6);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .chat-actions {
    display: flex;
    gap: 0.25rem;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover .chat-actions {
    opacity: 1;
  }
`

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0; /* Prevent flex item from overflowing */
`

const WelcomeScreen = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 2rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.8);
  overflow-y: auto;

  h2 {
    font-size: 2rem;
    margin-bottom: 1rem;
    background: linear-gradient(135deg, #553c9a 0%, #8367c7 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  p {
    color: rgba(255, 255, 255, 0.6);
    margin-bottom: 2rem;
    max-width: 600px;
  }
`

const SuggestedPrompts = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  max-width: 800px;
  width: 100%;
`

const PromptCard = styled.div<PromptCardProps & StyledProps>`
  background: rgba(85, 60, 154, 0.1);
  border: 1px solid rgba(85, 60, 154, 0.3);
  border-radius: 12px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(85, 60, 154, 0.2);
    border-color: rgba(85, 60, 154, 0.4);
    transform: translateY(-2px);
  }

  h4 {
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
    color: white;
  }

  p {
    font-size: 0.9rem;
    margin: 0;
    color: rgba(255, 255, 255, 0.7);
  }
`

const ChatContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
`

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;

  /* Start from the top */
  justify-content: flex-start;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #2a2a2a;
  }

  &::-webkit-scrollbar-thumb {
    background: #555;
    border-radius: 3px;
  }
`

const MessageBubble = styled.div<{ $isUser: boolean; $isSystem?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  ${(props) => (props.$isUser ? 'flex-direction: row-reverse;' : '')}
  ${(props) => (props.$isSystem ? 'justify-content: center; margin: 0.5rem 0;' : '')}

  .avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${(props) => (props.$isUser ? '#553c9a' : props.$isSystem ? '#ff8800' : '#2a2a2a')};
    color: white;
    flex-shrink: 0;
    margin-top: 0.25rem;
    ${(props) => (props.$isSystem ? 'display: none;' : '')}
  }

  .message-content {
    max-width: ${(props) => (props.$isSystem ? '90%' : '70%')};
    background: ${(props) =>
      props.$isSystem
        ? 'rgba(255, 136, 0, 0.1)'
        : props.$isUser
        ? 'rgba(85, 60, 154, 0.2)'
        : 'rgba(255, 255, 255, 0.05)'};
    border: 1px solid
      ${(props) =>
        props.$isSystem
          ? 'rgba(255, 136, 0, 0.2)'
          : props.$isUser
          ? 'rgba(85, 60, 154, 0.3)'
          : 'rgba(255, 255, 255, 0.1)'};
    border-radius: 12px;
    padding: ${(props) => (props.$isSystem ? '0.5rem 1rem' : '0.75rem 1rem')};
    color: ${(props) => (props.$isSystem ? 'rgba(255, 136, 0, 0.9)' : 'white')};
    line-height: 1.5;
    word-wrap: break-word;
    text-align: ${(props) => (props.$isSystem ? 'center' : props.$isUser ? 'right' : 'left')};
    font-size: ${(props) => (props.$isSystem ? '0.85rem' : '1rem')};
    font-style: ${(props) => (props.$isSystem ? 'italic' : 'normal')};

    pre {
      background: rgba(0, 0, 0, 0.3);
      padding: 0.5rem;
      border-radius: 6px;
      margin: 0.5rem 0;
      overflow-x: auto;
      font-size: 0.9rem;
    }

    code {
      background: rgba(0, 0, 0, 0.3);
      padding: 0.2rem 0.4rem;
      border-radius: 4px;
      font-size: 0.9rem;
    }
  }
`

const InputContainer = styled.div`
  padding: 1rem 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: #2a2a2a;
  display: flex;
  flex-direction: column;
  width: 100%;
  border-bottom-right-radius: 12px;

  .input-wrapper {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
  }

  .form-control {
    background: #1a1a1a;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    color: white;
    padding: 0.75rem 1rem;
    font-size: 0.95rem;
    flex: 1;
    resize: none;
    min-height: 48px;
    max-height: 120px;
    overflow: auto;

    &:focus {
      background: #1a1a1a;
      border-color: #553c9a;
      box-shadow: 0 0 0 0.2rem rgba(85, 60, 154, 0.2);
      color: white;
    }

    &::placeholder {
      color: rgba(255, 255, 255, 0.5);
    }
  }

  .send-button {
    background: #553c9a;
    border: none;
    border-radius: 8px;
    padding: 0.75rem 1rem;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    min-width: 48px;

    &:hover:not(:disabled) {
      background: #6d4ca0;
      transform: translateY(-1px);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
`

const LoadingMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.7);
  font-style: italic;
  padding: 0.5rem 0;
`

export const AIChatModal: React.FC<AIChatModalProps> = ({ show, onHide }) => {
  const [inputValue, setInputValue] = useState('')
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null)
  const [isNewChat, setIsNewChat] = useState(false)
  const [editingChat, setEditingChat] = useState<{
    id: string
    name: string
    description?: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Track pending messages per chat
  const [pendingMessagesByChatId, setPendingMessagesByChatId] = useState<
    Record<string, DisplayMessage[]>
  >({})
  // Track which chats are currently loading
  const [loadingChats, setLoadingChats] = useState<Set<string>>(new Set())
  // Track if this chat has been initially scrolled
  const [initialScrollDone, setInitialScrollDone] = useState<Record<string, boolean>>({})
  const [isNearBottom, setIsNearBottom] = useState(true)
  const chatContainerRef = useRef<HTMLDivElement>(null)

  // Get current chat's pending messages (memoized to keep stable dependency refs)
  const pendingMessages = useMemo<DisplayMessage[]>(() => {
    if (!selectedChatId) return []
    return pendingMessagesByChatId[selectedChatId] || []
  }, [selectedChatId, pendingMessagesByChatId])

  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Get project and dataset information
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: projectData } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const { data: modelTreeData } = useGetModelTreeQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const datasetSources = useAppSelector(selectDatasetSources)

  // Chat API hooks
  const { data: chatsData } = useListChatsQuery()
  const {
    data: chatHistory,
    isLoading: isLoadingHistory,
    refetch: refetchHistory,
  } = useGetChatHistoryQuery(selectedChatId || '', {
    skip: !selectedChatId || isNewChat,
  })

  // Only use chat history if the messages actually belong to the currently selected chat
  const currentChatHistory = useMemo(() => {
    if (chatHistory && selectedChatId && !isLoadingHistory) {
      return {
        ...chatHistory,
        messages: chatHistory.messages.filter((msg: TransformedChatMessage) => {
          // Check if the message belongs to the current chat using the original ChatID
          // Since messages are transformed, we need to check the original ChatID property
          return (msg as any).ChatID === selectedChatId
        }),
      }
    }
    return null
  }, [chatHistory, selectedChatId, isLoadingHistory])
  const [createChat] = useCreateChatMutation()
  const [updateChat] = useUpdateChatMutation()
  const [deleteChat] = useDeleteChatMutation()
  const [sendMessage] = useSendMessageMutation()

  // Track message counts to determine when to clear pending messages
  const [messageCountWhenLoading, setMessageCountWhenLoading] = useState<Record<string, number>>({})

  // Clear loading state and pending messages when we receive a complete response
  useEffect(() => {
    if (currentChatHistory?.messages && selectedChatId && loadingChats.has(selectedChatId)) {
      const pendingMessages = pendingMessagesByChatId[selectedChatId] || []
      const initialMessageCount = messageCountWhenLoading[selectedChatId] || 0

      if (pendingMessages.length > 0) {
        // Get the last two messages from chat history to check if we have user + assistant pair
        const messages = currentChatHistory.messages
        const currentMessageCount = messages.length

        // We should have at least 2 more messages than when we started loading (user + assistant)
        if (currentMessageCount >= initialMessageCount + 2) {
          const lastMessage = messages[messages.length - 1]
          const secondLastMessage = messages[messages.length - 2]

          // Check if we have a complete user-assistant exchange
          if (
            lastMessage &&
            lastMessage.role === 'assistant' &&
            secondLastMessage &&
            secondLastMessage.role === 'user'
          ) {
            // Clear loading state and pending messages
            setLoadingChats((prev) => {
              const next = new Set(prev)
              next.delete(selectedChatId)
              return next
            })
            setPendingMessagesByChatId((prev) => {
              const next = { ...prev }
              delete next[selectedChatId]
              return next
            })
            setMessageCountWhenLoading((prev) => {
              const next = { ...prev }
              delete next[selectedChatId]
              return next
            })
          }
        }
      }
    }
  }, [
    currentChatHistory,
    selectedChatId,
    pendingMessagesByChatId,
    loadingChats,
    messageCountWhenLoading,
  ])

  // Scroll to bottom only on initial chat load
  useEffect(() => {
    if (!selectedChatId || initialScrollDone[selectedChatId]) return

    const chatContainer = messagesEndRef.current
    if (chatContainer) {
      chatContainer.scrollTop = chatContainer.scrollHeight
      setInitialScrollDone((prev) => ({
        ...prev,
        [selectedChatId]: true,
      }))
    }
  }, [selectedChatId, currentChatHistory, initialScrollDone])

  // Reset initial scroll state when modal is closed
  useEffect(() => {
    if (!show) {
      setInitialScrollDone({})
      setInputValue('')
      setPendingMessagesByChatId({})
      setLoadingChats(new Set())
      setMessageCountWhenLoading({})
      setError(null)
    }
  }, [show])

  // Handle scroll events to determine if we're near the bottom
  const handleScroll = () => {
    const container = chatContainerRef.current
    if (container) {
      const { scrollTop, scrollHeight, clientHeight } = container
      // Consider "near bottom" if within 100px of the bottom
      const bottom = scrollHeight - scrollTop - clientHeight < 100
      setIsNearBottom(bottom)
    }
  }

  // Scroll to bottom if we're already near bottom or it's a new chat
  useEffect(() => {
    const container = chatContainerRef.current
    if (container && (isNearBottom || !currentChatHistory?.messages?.length)) {
      container.scrollTop = container.scrollHeight
    }
  }, [currentChatHistory?.messages, pendingMessages, isNearBottom])

  // Scroll to bottom when modal opens
  useEffect(() => {
    if (show) {
      const container = chatContainerRef.current
      if (container) {
        container.scrollTop = container.scrollHeight
        setIsNearBottom(true)
      }
    }
  }, [show])

  const handleSendMessage = async () => {
    if (!inputValue.trim() || !selectedChatId || isNewChat) return
    if (loadingChats.has(selectedChatId)) return

    // Check if we have required project information
    if (!selectedProjectId || !projectData?.datasetid) {
      setError('Please select a project and dataset before chatting with the AI assistant.')
      return
    }

    const messageContent = inputValue.trim()
    setInputValue('')

    // Add user message immediately to UI
    const tempId = Date.now().toString()
    const optimisticUserMessage: DisplayMessage = {
      id: `temp-${tempId}-user`,
      role: 'user',
      content: messageContent,
    }

    // Update pending messages for this chat
    setPendingMessagesByChatId((prev) => {
      const updated = {
        ...prev,
        [selectedChatId]: [...(prev[selectedChatId] || []), optimisticUserMessage],
      }
      return updated
    })

    // Set loading state for this chat and track initial message count
    setLoadingChats((prev) => {
      const next = new Set(prev).add(selectedChatId)
      return next
    })

    // Track the current message count when we start loading
    setMessageCountWhenLoading((prev) => ({
      ...prev,
      [selectedChatId]: currentChatHistory?.messages?.length || 0,
    }))

    try {
      await sendMessage({
        chat_id: selectedChatId,
        message: messageContent,
        model_type: 'qwen2.5:14b',
        project_id: selectedProjectId,
        dataset_id: projectData.datasetid,
        metadata_filter:
          datasetSources && datasetSources.length > 0 && !datasetSources.includes('All')
            ? {
                datasource: datasetSources,
              }
            : undefined,
        model_id: modelTreeData?.model_tree[0]?.model?.id || 'YOUR_MODEL_ID',
      }).unwrap()

      // If this is the first message in a chat, update the chat name
      const currentChat = chatsData?.chats.find((chat) => chat.id === selectedChatId)
      if (currentChat?.name === 'New Chat') {
        const newName = generateChatName(messageContent)
        await updateChat({
          chatId: selectedChatId,
          chatData: {
            name: newName,
            description: currentChat.description,
          },
        }).unwrap()
      }

      // Set a timeout to clear loading state if we don't get a response within reasonable time
      setTimeout(() => {
        setLoadingChats((prev) => {
          if (prev.has(selectedChatId)) {
            const next = new Set(prev)
            next.delete(selectedChatId)
            return next
          }
          return prev
        })
        setPendingMessagesByChatId((prev) => {
          if (prev[selectedChatId]) {
            const next = { ...prev }
            delete next[selectedChatId]
            return next
          }
          return prev
        })
        setMessageCountWhenLoading((prev) => {
          if (prev[selectedChatId] !== undefined) {
            const next = { ...prev }
            delete next[selectedChatId]
            return next
          }
          return prev
        })
      }, 30000) // 30 second timeout
    } catch (err) {
      setError('Failed to send message')
      setInputValue(messageContent)
      // Remove the optimistic message on error
      setPendingMessagesByChatId((prev) => {
        const chatMessages = prev[selectedChatId] || []
        const updated = {
          ...prev,
          [selectedChatId]: chatMessages.filter((msg) => msg.id !== optimisticUserMessage.id),
        }
        return updated
      })
      // Clear loading state on error
      setLoadingChats((prev) => {
        const next = new Set(prev)
        next.delete(selectedChatId)
        return next
      })
    }
  }

  // Helper function to generate chat name from message
  const generateChatName = (message: string): string => {
    // Remove special characters and extra spaces
    const cleaned = message
      .replace(/[^\w\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
    // Take first 5-6 words or 50 characters, whichever is shorter
    const words = cleaned.split(' ').slice(0, 6)
    let name = words.join(' ')
    if (name.length > 50) {
      name = name.slice(0, 47) + '...'
    }
    return name
  }

  const handleNewChat = () => {
    setIsNewChat(true)
    setSelectedChatId(null)
    setInputValue('')
    setError(null)
    setPendingMessagesByChatId({})
  }

  const handleStartNewChat = async (initialMessage?: string) => {
    try {
      const messageContent = initialMessage?.trim()
      if (!messageContent) return

      // Create the chat immediately with the generated name
      const chatName = generateChatName(messageContent)
      const result = await createChat({
        name: chatName,
        description: '',
      }).unwrap()

      // Switch to the chat view immediately and clear any previous state
      setSelectedChatId(result.id)
      setIsNewChat(false)
      setInputValue('')

      // Clear any lingering state from previous chats
      setPendingMessagesByChatId({})
      setLoadingChats(new Set())
      setMessageCountWhenLoading({})

      // Add user message immediately to UI
      const tempId = Date.now().toString()
      const optimisticUserMessage: DisplayMessage = {
        id: `temp-${tempId}-user`,
        role: 'user',
        content: messageContent,
      }

      setPendingMessagesByChatId((prev) => ({
        ...prev,
        [result.id]: [optimisticUserMessage],
      }))

      setLoadingChats((prev) => new Set(prev).add(result.id))

      // Track the current message count when we start loading (should be 0 for new chat)
      setMessageCountWhenLoading((prev) => ({
        ...prev,
        [result.id]: 0,
      }))

      try {
        await sendMessage({
          chat_id: result.id,
          message: messageContent,
          model_type: 'qwen2.5:14b',
          project_id: selectedProjectId,
          dataset_id: projectData?.datasetid,
          metadata_filter:
            datasetSources && datasetSources.length > 0 && !datasetSources.includes('All')
              ? {
                  datasource: datasetSources,
                }
              : undefined,
          model_id: modelTreeData?.model_tree[0]?.model?.id || 'YOUR_MODEL_ID',
        }).unwrap()

        // Force refetch history after sending message
        await refetchHistory()

        // Set a timeout to clear loading state if we don't get a response within reasonable time
        setTimeout(() => {
          setLoadingChats((prev) => {
            if (prev.has(result.id)) {
              const next = new Set(prev)
              next.delete(result.id)
              return next
            }
            return prev
          })
          setPendingMessagesByChatId((prev) => {
            if (prev[result.id]) {
              const next = { ...prev }
              delete next[result.id]
              return next
            }
            return prev
          })
          setMessageCountWhenLoading((prev) => {
            if (prev[result.id] !== undefined) {
              const next = { ...prev }
              delete next[result.id]
              return next
            }
            return prev
          })
        }, 30000) // 30 second timeout
      } catch (err) {
        setError('Failed to send message')
        setPendingMessagesByChatId((prev) => {
          const next = { ...prev }
          delete next[result.id]
          return next
        })
        setLoadingChats((prev) => {
          const next = new Set(prev)
          next.delete(result.id)
          return next
        })
      }
    } catch (err) {
      setError('Failed to create chat')
    }
  }

  const handleDeleteChat = async (chatId: string) => {
    try {
      await deleteChat(chatId).unwrap()
      if (selectedChatId === chatId) {
        setSelectedChatId(null)
        setInputValue('')
        setPendingMessagesByChatId((prev) => {
          const next = { ...prev }
          delete next[chatId]
          return next
        })
        setLoadingChats((prev) => {
          const next = new Set(prev)
          next.delete(chatId)
          return next
        })
      }
    } catch (err) {
      setError('Failed to delete chat')
    }
  }

  const handleUpdateChat = async () => {
    if (!editingChat) return
    try {
      await updateChat({
        chatId: editingChat.id,
        chatData: {
          name: editingChat.name,
          description: editingChat.description,
        },
      }).unwrap()
      setEditingChat(null)
    } catch (err) {
      setError('Failed to update chat')
    }
  }

  const suggestedPrompts = [
    {
      title: 'Analyze Project Data',
      description: "Get insights about your current project's data distribution and quality",
      message:
        "Can you analyze my current project's data and provide insights about its distribution and quality?",
    },
    {
      title: 'Model Performance',
      description: "Understand your model's performance metrics and get improvement suggestions",
      message: 'How is my current model performing? Can you suggest ways to improve it?',
    },
    {
      title: 'Dataset Recommendations',
      description: 'Get recommendations for improving your dataset',
      message: 'What improvements can I make to my dataset to get better model performance?',
    },
    {
      title: 'Computer Vision Help',
      description: 'Ask questions about computer vision concepts and techniques',
      message: 'Can you explain how object detection works and what are the best practices?',
    },
  ]

  return (
    <>
      <StyledModal show={show} onHide={onHide} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <MessageCircle size={20} />
            AI Assistant
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex flex-column p-0 overflow-hidden">
          <div style={{ display: 'flex', width: '100%', height: '100%' }}>
            <ChatSidebar>
              <NewChatButton onClick={handleNewChat}>
                <Plus size={16} />
                New Chat
              </NewChatButton>
              <ChatList>
                {chatsData?.chats.map((chat) => (
                  <ChatItem
                    key={chat.id}
                    $isSelected={chat.id === selectedChatId && !isNewChat}
                    onClick={() => {
                      setSelectedChatId(chat.id)
                      setIsNewChat(false)
                    }}
                  >
                    <div className="chat-info">
                      <div className="chat-name">{chat.name}</div>
                      {chat.description && (
                        <div className="chat-description">{chat.description}</div>
                      )}
                    </div>
                    <div className="chat-actions">
                      <Button
                        variant="link"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          setEditingChat(chat)
                        }}
                      >
                        <Edit2 size={14} />
                      </Button>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteChat(chat.id)
                        }}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </ChatItem>
                ))}
              </ChatList>
            </ChatSidebar>
            <MainContent>
              {!selectedChatId || isNewChat ? (
                <>
                  <WelcomeScreen>
                    <h2>Welcome to AI Assistant</h2>
                    <p>
                      I'm here to help you with your computer vision projects. You can ask me
                      anything about your data, models, or general computer vision concepts.
                    </p>
                    <SuggestedPrompts>
                      {suggestedPrompts.map((prompt, index) => (
                        <PromptCard key={index} onClick={() => handleStartNewChat(prompt.message)}>
                          <h4>{prompt.title}</h4>
                          <p>{prompt.description}</p>
                        </PromptCard>
                      ))}
                    </SuggestedPrompts>
                  </WelcomeScreen>
                  <InputContainer>
                    {error && (
                      <Alert
                        variant="danger"
                        className="mb-3"
                        onClose={() => setError(null)}
                        dismissible
                      >
                        {error}
                      </Alert>
                    )}
                    <div className="input-wrapper">
                      <Form.Control
                        as="textarea"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault()
                            if (!selectedChatId || isNewChat) {
                              handleStartNewChat(inputValue)
                            } else {
                              handleSendMessage()
                            }
                          }
                        }}
                        placeholder={
                          !selectedProjectId || !projectData?.datasetid
                            ? 'Select a project and dataset to start chatting...'
                            : 'Type your message...'
                        }
                        rows={1}
                        disabled={Boolean(!selectedProjectId || !projectData?.datasetid)}
                      />
                      <Button
                        className="send-button"
                        onClick={() => {
                          if (!selectedChatId || isNewChat) {
                            handleStartNewChat(inputValue)
                          } else {
                            handleSendMessage()
                          }
                        }}
                        disabled={
                          !inputValue.trim() || !selectedProjectId || !projectData?.datasetid
                        }
                      >
                        {selectedChatId && loadingChats.has(selectedChatId) ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <Send size={16} />
                        )}
                      </Button>
                    </div>
                  </InputContainer>
                </>
              ) : (
                <ChatContainer>
                  <MessagesContainer ref={chatContainerRef} onScroll={handleScroll}>
                    {isLoadingHistory ? (
                      <div className="text-center p-4">
                        <Spinner animation="border" />
                        <p>Loading chat history...</p>
                      </div>
                    ) : (!currentChatHistory?.messages ||
                        currentChatHistory.messages.length === 0) &&
                      !pendingMessages.length ? (
                      <div
                        style={{
                          textAlign: 'center',
                          color: 'rgba(255, 255, 255, 0.5)',
                          fontStyle: 'italic',
                          padding: '2rem',
                        }}
                      >
                        <div style={{ marginBottom: '1rem' }}>
                          Start a conversation with the AI assistant. Ask questions about computer
                          vision, your project, or anything else!
                        </div>
                      </div>
                    ) : (
                      <>
                        {(currentChatHistory?.messages || []).map((message, index) => (
                          <MessageBubble
                            key={message.id || message.ID}
                            $isUser={message.role === 'user'}
                          >
                            <div className="avatar">
                              {message.role === 'user' ? <User size={16} /> : <Bot size={16} />}
                            </div>
                            <div className="message-content">
                              {message.content ||
                                (message.role === 'user' ? message.Prompt : message.Response)}
                            </div>
                          </MessageBubble>
                        ))}
                        {pendingMessages?.map((message, index) => (
                          <MessageBubble key={message.id} $isUser={message.role === 'user'}>
                            <div className="avatar">
                              {message.role === 'user' ? <User size={16} /> : <Bot size={16} />}
                            </div>
                            <div className="message-content">{message.content}</div>
                          </MessageBubble>
                        ))}

                        {selectedChatId && loadingChats.has(selectedChatId) && (
                          <MessageBubble $isUser={false}>
                            <div className="avatar">
                              <Bot size={16} />
                            </div>
                            <div className="message-content">
                              <LoadingMessage>
                                <Spinner animation="border" size="sm" />
                                AI is thinking...
                              </LoadingMessage>
                            </div>
                          </MessageBubble>
                        )}
                      </>
                    )}
                    <div ref={messagesEndRef} style={{ float: 'left', clear: 'both' }} />
                  </MessagesContainer>

                  <InputContainer>
                    {error && (
                      <Alert
                        variant="danger"
                        className="mb-3"
                        onClose={() => setError(null)}
                        dismissible
                      >
                        {error}
                      </Alert>
                    )}
                    <div className="input-wrapper">
                      <Form.Control
                        as="textarea"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault()
                            if (!selectedChatId || isNewChat) {
                              handleStartNewChat(inputValue)
                            } else {
                              handleSendMessage()
                            }
                          }
                        }}
                        placeholder={
                          !selectedProjectId || !projectData?.datasetid
                            ? 'Select a project and dataset to start chatting...'
                            : 'Type your message...'
                        }
                        rows={1}
                        disabled={Boolean(!selectedProjectId || !projectData?.datasetid)}
                      />
                      <Button
                        className="send-button"
                        onClick={() => {
                          if (!selectedChatId || isNewChat) {
                            handleStartNewChat(inputValue)
                          } else {
                            handleSendMessage()
                          }
                        }}
                        disabled={
                          !inputValue.trim() || !selectedProjectId || !projectData?.datasetid
                        }
                      >
                        {selectedChatId && loadingChats.has(selectedChatId) ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <Send size={16} />
                        )}
                      </Button>
                    </div>
                  </InputContainer>
                </ChatContainer>
              )}
            </MainContent>
          </div>
        </Modal.Body>
      </StyledModal>

      {/* Edit Chat Modal */}
      <Modal show={!!editingChat} onHide={() => setEditingChat(null)} size="sm" centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Chat</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Chat Name</Form.Label>
              <Form.Control
                type="text"
                value={editingChat?.name || ''}
                onChange={(e) =>
                  setEditingChat((prev) => (prev ? { ...prev, name: e.target.value } : null))
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={editingChat?.description || ''}
                onChange={(e) =>
                  setEditingChat((prev) => (prev ? { ...prev, description: e.target.value } : null))
                }
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditingChat(null)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpdateChat}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
